import { isRecord, SIMPLERSELL_ATHENA_GRAPHQL_URL } from './athena-utils';

export { isRecord } from './athena-utils';

const ATHENA_REVALIDATE_SECONDS = 15 * 60;

const ATHENA_URLS = {
  production: 'https://graphql.apps.opendoor.com/api/graphql',
  development: SIMPLERSELL_ATHENA_GRAPHQL_URL,
  localdev: process.env.ODFE_ATHENA_URL || 'http://localhost:4000/api/graphql',
  qa: SIMPLERSELL_ATHENA_GRAPHQL_URL,
  staging: SIMPLERSELL_ATHENA_GRAPHQL_URL,
  test: SIMPLERSELL_ATHENA_GRAPHQL_URL,
} as const;

export type DeployEnv = keyof typeof ATHENA_URLS;

type NextFetchRequestInit = RequestInit & {
  next?: {
    revalidate: number;
  };
};

function isDeployEnv(env: string): env is DeployEnv {
  return Object.hasOwn(ATHENA_URLS, env);
}

export function currentAthenaEnv(): DeployEnv {
  const env = process.env.NEXT_PUBLIC_DEPLOY_ENV;
  if (!env) return 'production';
  if (isDeployEnv(env)) return env;
  throw new Error(`Unrecognized NEXT_PUBLIC_DEPLOY_ENV for Athena: ${env}`);
}

export function athenaUrl(deployEnv: DeployEnv = currentAthenaEnv()) {
  return ATHENA_URLS[deployEnv];
}

export async function athenaGraphqlRequest({
  query,
  variables,
  operationName,
  deployEnv = currentAthenaEnv(),
}: {
  query: string;
  variables: Record<string, unknown>;
  operationName: string;
  deployEnv?: DeployEnv;
}): Promise<unknown> {
  const requestInit: NextFetchRequestInit = {
    method: 'POST',
    next: { revalidate: ATHENA_REVALIDATE_SECONDS },
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'apollographql-client-name': 'beacon',
    },
    body: JSON.stringify({ query, variables, operationName }),
  };

  const response = await fetch(athenaUrl(deployEnv), requestInit);

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  const payload: unknown = await response.json();
  if (isRecord(payload) && Array.isArray(payload.errors) && payload.errors.length) {
    throw new Error(`Athena returned GraphQL errors for ${operationName}`);
  }
  return payload;
}
